import { useEffect, useMemo, useState } from "react";
import { useLiveFeed } from "@/stores/liveFeed";
import type { Service } from "@/lib/types";

// Snapshots older than this (ms) mean the live feed has gone quiet. The edge
// publishes ~5 Hz and the server flips service.sig to "stale" at 8s, so 10s
// here is a conservative "the whole feed stopped" signal for the widget.
export const REALTIME_STALE_MS = 10_000;

export interface RealtimeTotal {
  /** Summed apparent power across active services, in VA. */
  apparentVa: number;
  /** Per-leg summed current (A): [L1, L2, L3]. */
  ampsPerLeg: [number, number, number];
  /** Largest of the three per-leg sums (A) — the limiting phase. */
  ampsMaxLeg: number;
  /** Number of active services contributing to the totals. */
  serviceCount: number;
  /** True when no snapshot has arrived within REALTIME_STALE_MS. */
  stale: boolean;
}

/**
 * Per-service apparent power (VA). ACUVIM3 meters report `s` directly;
 * ACUVIM2R meters have no apparent-power register, so apparent power is
 * derived as the per-phase sum of voltage x current — the same definition
 * the /api/dyno/total-load history endpoint uses, so the realtime headline
 * and the chart's history agree.
 */
export function serviceApparentVa(svc: Service): number {
  if (typeof svc.s === "number") return svc.s;
  const { v, i } = svc;
  if (!v || !i) return 0;
  let va = 0;
  for (let leg = 0; leg < 3; leg++) {
    const vl = v[leg];
    const il = i[leg];
    if (typeof vl === "number" && typeof il === "number") va += vl * il;
  }
  return va;
}

/**
 * Pure reduction: sum apparent power and per-leg current across the active
 * services. When `eventSystemIds` is given, only services whose system_id is
 * in that set are summed — the admin WS feed carries every event's services,
 * so an unscoped sum mixes events. The caller passes the system_ids of the
 * selected event (from /api/services — the same authoritative list the
 * dashboard tiles render from). Scoping by the WS feed's own `window_id` is
 * unreliable: that field carries the edge realtime payload's `event_id`, not
 * the cloud window id the dropdown uses. Omitting the set sums all (legacy).
 * Exported separately from the hook so it can be unit-tested without a React
 * render or the store.
 */
export function computeRealtimeTotal(
  services: Service[],
  eventSystemIds?: ReadonlySet<string> | null,
): Omit<RealtimeTotal, "stale"> {
  const active = services.filter(
    (s) =>
      s.left_at == null &&
      (eventSystemIds == null || eventSystemIds.has(s.system_id)),
  );
  let apparentVa = 0;
  const ampsPerLeg: [number, number, number] = [0, 0, 0];
  for (const svc of active) {
    apparentVa += serviceApparentVa(svc);
    const i = svc.i;
    if (i) {
      for (let leg = 0; leg < 3; leg++) {
        const il = i[leg];
        if (typeof il === "number") ampsPerLeg[leg] += il;
      }
    }
  }
  return {
    apparentVa,
    ampsPerLeg,
    ampsMaxLeg: Math.max(...ampsPerLeg),
    serviceCount: active.length,
  };
}

/**
 * Realtime total show load, summed live from the dyno WS snapshot across the
 * active ACUVIM services of the selected event. Independent of any chart
 * *range* selection — the headline reflects the whole event, not the chart's
 * zoom (R8) — but it IS scoped to the selected event: the admin WS feed
 * carries every event's services, so `eventSystemIds` (the selected event's
 * membership system_ids) keeps the headline on the event chosen in the
 * dashboard dropdown rather than summing all live events at once.
 */
export function useRealtimeTotal(
  eventSystemIds?: ReadonlySet<string> | null,
): RealtimeTotal {
  const services = useLiveFeed((s) => s.services);
  const lastSnapshotAt = useLiveFeed((s) => s.lastSnapshotAt);

  // Staleness needs a wall-clock tick: when snapshots STOP arriving, no store
  // update fires, so `stale` would never flip on its own. A slow interval
  // re-renders the consumer so staleness resolves even in a dead feed — 5s
  // resolution is imperceptible against the 10s threshold and avoids a 1 Hz
  // idle re-render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);

  // Keyed on `services` + `eventSystemIds` — the staleness tick re-renders
  // without paying for a fresh reduction when neither the feed nor the
  // selected event has changed. (Callers must pass a stable Set ref.)
  const totals = useMemo(
    () => computeRealtimeTotal(Object.values(services), eventSystemIds),
    [services, eventSystemIds],
  );
  const stale =
    lastSnapshotAt == null || now - lastSnapshotAt > REALTIME_STALE_MS;
  return { ...totals, stale };
}
